import { useTranslations } from 'next-intl';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { Header } from '@/components/layout/header';
import { ModalPortal } from '@/components/layout/modals/ModalPortal';
import { Button } from '@/components/ui/button/Button';
import CheckBox from '@/components/ui/input/CheckBox';
import { cn } from '@/utils/cn';
import { guidelineLink, privacyLink, termLink } from '@/utils/legal-link';

type Props = {
  isOpen: boolean;
  onClick: () => void;
};

type TermsFormValues = {
  agreeToTerms: boolean;
};

export const MigrationTermsModal = ({ isOpen, onClick }: Props) => {
  const t = useTranslations();
  const { register, watch, handleSubmit } = useForm<TermsFormValues>({
    defaultValues: {
      agreeToTerms: false,
    },
  });

  const watchedAgreeToTerms = watch('agreeToTerms');

  const isAllFilled = Boolean(watchedAgreeToTerms);

  const onSubmit: SubmitHandler<TermsFormValues> = async () => {
    // TODO:IDP APIが叩けたら、規約同意APIを呼び出し、dataを送る
    onClick();
  };

  return (
    <ModalPortal isOpen={isOpen} withHeaderLayout={true}>
      <form onSubmit={handleSubmit(onSubmit)} className='w-full'>
        <Header logo bgVariant='gray-deep' />
        <div className='flex flex-col gap-8 px-5 py-9'>
          <div className='flex flex-col items-center gap-3'>
            <span className='text-head-24 text-kl-black-deep'>
              {t('livenode-id-migration-terms-update_title')}
            </span>
            <span className='whitespace-pre-line text-center text-body-14 text-kl-black-deep'>
              {t('livenode-id-migration-terms-update_description')}
            </span>
          </div>
          <div className='flex flex-col gap-2'>
            <span className='pl-2 text-label-16'>
              {t('livenode-id-migration-terms-update_link')}
            </span>
            <div className='border-stroke-subtle h-56 w-full overflow-y-auto rounded-md border px-3 py-2'>
              <p className='whitespace-pre-line text-body-12'>
                {t.rich('klew_terms', { privacy_link: privacyLink })}
              </p>
            </div>
          </div>
          <div className='flex flex-col gap-2 p-2'>
            <span className='text-label-16'>{t('common_privacy-policy')}</span>
            <span className='text-body-12'>
              {t('livenode-id-migration-privacy-policy_description')}
            </span>
          </div>
          <div
            className={cn(
              'rounded-lg border border-kl-gray-subtle p-5',
              isAllFilled ? 'bg-white' : 'bg-func-primary/10',
            )}
          >
            <CheckBox register={register('agreeToTerms', { required: true })} size='lg'>
              <span className='text-label-14'>
                {t.rich('livenode-id-migration-terms-update_check', {
                  term_link: termLink,
                  privacy_link: privacyLink,
                  guideline_link: guidelineLink,
                })}
              </span>
            </CheckBox>
          </div>
          <div className='px-5 py-6'>
            <Button
              type='submit'
              label={t('livenode-id-migration-next_button')}
              color='gradient'
              disabled={!isAllFilled}
              fullWidth
            />
          </div>
        </div>
      </form>
    </ModalPortal>
  );
};
